import { dataKeyToPrePromptTransformerConfig } from "src/config/dataKeyFunctions/pre-prompt-transformer-configs";
import { dataKeyToOutputTransformer } from "src/config/dataKeyFunctions/question-level-functions/answer-item-element/output-transformers";
import { dataKeyToOnUpload } from "src/config/dataKeyFunctions/question-level-functions/parsed-file-upload-element/on-upload";
import { dataKeyToQuestionTransformerConfig } from "src/config/dataKeyFunctions/question-level-functions/question-transformer-configs";
import {
  DataKey,
  DataKeyQuestion,
  DocumentTemplate,
  TemplatesConfig,
} from "../stores/models";
import {
  AnswerItemsElement,
  DateElement,
  FileUploadElement,
  ParsedFileUploadElement,
  SelectElement,
  TableElement,
  Template,
  TemplateConfig,
  TemplateQuestion,
  TextFieldElement,
} from "../types";

const createTransformerConfig = (
  questionId: string,
  templatesConfig?: TemplateConfig
) => {
  if (!templatesConfig) return undefined;
  const config =
    dataKeyToQuestionTransformerConfig(templatesConfig)[questionId];
  if (!config) return undefined;

  return {
    type: "default" as const,
    inputs: config.inputs,
    transformer: config.transformer,
  };
};

export const transformTemplatesConfigToFormat = (
  templatesConfig: TemplatesConfig
): TemplateConfig => {
  const result: TemplateConfig = {} as TemplateConfig;

  templatesConfig?.forEach((template: DocumentTemplate) => {
    result[template.templateKey] = transformTemplateToFormat(template, result);
  });

  return result;
};

export const transformTemplateToFormat = (
  templateConfig: DocumentTemplate,
  templatesConfig?: TemplateConfig
): Template => {
  let result: Template = {} as Template;

  const questions = attachActionsToQuestions(
    templateConfig.dataKey,
    templatesConfig
  );

  result = {
    ...templateConfig,
    questions,
  };

  checkForCyclicalDependencies(result);

  return result;
};

export const attachActionsToQuestions = (
  dataKey: { order?: number | null; dataKey: DataKey }[] | undefined,
  templatesConfig?: TemplateConfig
): TemplateQuestion[] => {
  if (!dataKey || dataKey.length === 0) {
    return [];
  }

  return (
    dataKey
      // Sort unnumbered items (order null/undefined) last
      .sort((a, b) => {
        if (a.order == null) return 1;
        if (b.order == null) return -1;
        return a.order - b.order;
      })
      .map((dataKeyItem) => {
        const { dataKey: keyData } = dataKeyItem;

        const element = transformQuestionToElement(
          keyData.question,
          templatesConfig
        );

        return {
          id: keyData.key,
          required: keyData.required,
          videoUrl: keyData.videoUrl || undefined,
          context: keyData.context?.filter(
            (ctx): ctx is string => ctx !== undefined
          ),
          element,
          prePromptTransformerConfig:
            dataKeyToPrePromptTransformerConfig[keyData.key],
          visibilityConditions: keyData.visibilityConditions,
        };
      })
  );
};

const transformQuestionToElement = (
  question: DataKeyQuestion,
  templatesConfig?: TemplateConfig
): TemplateQuestion["element"] => {
  const { id, type, options } = question;

  switch (type) {
    case "textField":
      const textFieldOptionsTyped = options as TextFieldElement["options"];
      return {
        id,
        type: "textField",
        options: {
          type: textFieldOptionsTyped.type,
          label: textFieldOptionsTyped.label,
          rows: textFieldOptionsTyped.rows,
          helperText: textFieldOptionsTyped.helperText,
          default: textFieldOptionsTyped.default,
        },
        transformerConfig: createTransformerConfig(id, templatesConfig),
      };

    case "select":
      const selectOptionsTyped = options as SelectElement["options"];

      const selectOptions = selectOptionsTyped?.options?.filter(
        (option): option is string => option !== undefined
      );

      return {
        id,
        type: "select",
        options: {
          label: selectOptionsTyped.label,
          options: selectOptions,
          default: selectOptionsTyped.default,
          multiple: selectOptionsTyped.multiple,
          helperText: selectOptionsTyped.helperText,
        },
        transformerConfig: createTransformerConfig(id, templatesConfig),
      };

    case "table":
      const tableOptionsTyped = options as TableElement["options"];
      const tableColumns =
        tableOptionsTyped.columns
          ?.filter(
            (col): col is typeof col & { name: string } =>
              col.name !== undefined
          )
          .map((col) => ({
            name: col.name,
            options: col.options
              ? {
                  flex: col.options.flex,
                  type: col.options.type as any,
                  maxWidth: col.options.maxWidth,
                  valueOptions: col.options.valueOptions?.filter(
                    (v): v is string => v !== undefined
                  ),
                  editable: col.options.editable,
                  multiline: col.options.multiline,
                }
              : undefined,
          })) || [];

      return {
        id,
        type: "table",
        options: {
          columns: tableColumns,
          idColumn: tableOptionsTyped.idColumn,
          default: tableOptionsTyped.default,
          label: tableOptionsTyped.label,
          helperText: tableOptionsTyped.helperText,
          addRowEnabled: tableOptionsTyped.addRowEnabled,
        },
        transformerConfig: createTransformerConfig(id, templatesConfig),
      };

    case "parsedFileUploadElement":
      const parsedFileUploadOptionsTyped =
        options as ParsedFileUploadElement["options"];
      return {
        id,
        type: "parsedFileUploadElement",
        options: {
          label: parsedFileUploadOptionsTyped.label,
          rows: parsedFileUploadOptionsTyped.rows,
          helperText: parsedFileUploadOptionsTyped.helperText,
          default: parsedFileUploadOptionsTyped.default,
          allowedFileTypes: parsedFileUploadOptionsTyped.allowedFileTypes,
          onUpload: dataKeyToOnUpload[id]?.onUpload,
        },
      };

    case "fileUpload":
      const fileUploadOptionsTyped = options as FileUploadElement["options"];
      return {
        id,
        type: "fileUpload",
        options: {
          label: fileUploadOptionsTyped.label,
          helperText: fileUploadOptionsTyped.helperText,
          allowedFileTypes: fileUploadOptionsTyped.allowedFileTypes || "",
          default: fileUploadOptionsTyped.default,
        },
      };

    case "answerItemsElement":
      const answerItemsElementOptionsTyped =
        options as AnswerItemsElement["options"];
      return {
        id,
        type: "answerItemsElement",
        options: {
          typeId: answerItemsElementOptionsTyped.typeId || "",
          outputTransformer: dataKeyToOutputTransformer[id]?.outputTransformer,
          label: answerItemsElementOptionsTyped.label,
          helperText: answerItemsElementOptionsTyped.helperText,
          default: answerItemsElementOptionsTyped.default,
          fields: answerItemsElementOptionsTyped.fields || [],
        },
      };

    case "dateInput":
      const dateInputOptionsTyped = options as DateElement["options"];
      const dateInputStyle = (options as any).style;
      return {
        id,
        type: "dateInput",
        options: {
          label: dateInputOptionsTyped.label,
          helperText: dateInputOptionsTyped.helperText,
          default: dateInputOptionsTyped.default,
          hideLabel: dateInputOptionsTyped.hideLabel,
        },
        style: dateInputStyle,
      };

    default:
      throw new Error(`Unknown element type: ${type}`);
  }
};

// Checks for cyclical dependencies to make sure that templates dont have dependencies that are elements of themselves
const checkForCyclicalDependencies = (template: Template) => {
  const dependencies = template.dependencies;
  const questionIds = template.questions.map((question) => question.id);
  if (dependencies && questionIds.some((id) => dependencies.includes(id))) {
    throw new Error(`Cyclical dependency found in template ${template.id}`);
  }
};

export const templatesWithUpload = (templatesConfig: TemplateConfig) =>
  Object.values(templatesConfig)
    .filter((template) => template.userUpload)
    .map((template) => template.templateKey);

